/**
 * Frontend mirror of functions-forms/src/ai-reply/kbPromptBudget.ts.
 * Keep both in sync — Cloud Functions cannot import from src/.
 */

const NAMED_ENTITIES = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  rsquo: '\u2019',
  lsquo: '\u2018',
  rdquo: '\u201D',
  ldquo: '\u201C',
  ndash: '\u2013',
  mdash: '\u2014',
  hellip: '\u2026',
  copy: '\u00A9',
  reg: '\u00AE',
  trade: '\u2122',
};

function decodeHtmlEntities(text) {
  let current = text || '';
  for (let pass = 0; pass < 3; pass += 1) {
    const next = current.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity) => {
      if (entity[0] === '#') {
        const code =
          entity[1] === 'x' || entity[1] === 'X'
            ? parseInt(entity.slice(2), 16)
            : parseInt(entity.slice(1), 10);
        if (!Number.isFinite(code) || code < 0) return match;
        try {
          return String.fromCodePoint(code);
        } catch {
          return match;
        }
      }
      return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
    });
    if (next === current) return next;
    current = next;
  }
  return current;
}

export const KB_PROMPT_MAX_CHARS = 200000;

export const RULES_START = '=== OPERATOR RULES (always included) ===';
export const RULES_END = '=== END OPERATOR RULES ===';
export const DRAFT_SEPARATOR = '---';

function looksLikeRulesBlock(text) {
  return /^(Conversation goal|Disqualifiers|Never say|Do not say|Pricing policy|What we do not(?: serve)?|What must the AI never|Do not serve|Hard rules?)\s*:/im.test(
    (text || '').trim()
  );
}

export function splitKbRulesAndProse(content) {
  let prose = content || '';
  let rules = '';

  const start = prose.indexOf(RULES_START);
  const end = prose.indexOf(RULES_END);
  if (start !== -1 && end > start) {
    rules = prose.slice(start + RULES_START.length, end).trim();
    prose = `${prose.slice(0, start)}${prose.slice(end + RULES_END.length)}`.trim();
  }

  const sep = `\n${DRAFT_SEPARATOR}\n`;
  const lastSep = prose.lastIndexOf(sep);
  if (lastSep !== -1) {
    const tail = prose.slice(lastSep + sep.length).trim();
    const head = prose.slice(0, lastSep).trim();
    if (tail && looksLikeRulesBlock(tail)) {
      rules = rules ? `${rules}\n\n${tail}` : tail;
      prose = head;
    }
  }

  return { rules, prose };
}

export function formatDraftAdditions(sections) {
  return (sections || [])
    .filter((s) => (s.label || '').trim() && (s.body || '').trim())
    .map((s) => `${s.label.trim().replace(/:+$/, '')}:\n${s.body.trim()}`)
    .join('\n\n');
}

function parseLabeledSections(text) {
  const sections = [];
  let current = null;
  for (const line of (text || '').split('\n')) {
    const labeled = line.match(/^([^:\n]{1,80}):\s*$/);
    if (labeled) {
      if (current) sections.push(current);
      current = { label: labeled[1].trim(), body: '' };
      continue;
    }
    if (current) {
      current.body = current.body ? `${current.body}\n${line}` : line;
    } else if (line.trim()) {
      current = { label: 'Notes', body: line };
    }
  }
  if (current) sections.push(current);
  return sections
    .map((s) => ({ label: s.label, body: s.body.trim() }))
    .filter((s) => s.body);
}

export function mergeRuleText(existing, incoming) {
  const map = new Map();
  for (const section of [...parseLabeledSections(existing), ...parseLabeledSections(incoming)]) {
    map.set(section.label.toLowerCase(), section);
  }
  return [...map.values()].map((s) => `${s.label}:\n${s.body}`).join('\n\n');
}

export function normalizeKbDocument(content) {
  const decoded = decodeHtmlEntities(content || '');
  const { rules, prose } = splitKbRulesAndProse(decoded);
  if (!rules) return decoded;
  return formatStoredKb(rules, prose);
}

export function formatStoredKb(rules, prose) {
  const rulesText = (rules || '').trim();
  const proseText = (prose || '').trim();
  if (!rulesText) return proseText;
  const block = `${RULES_START}\n${rulesText}\n${RULES_END}`;
  return proseText ? `${block}\n\n${proseText}\n` : `${block}\n`;
}

export function applyDraftToKb(kbContent, sections) {
  return writeRulesIntoKb(kbContent, formatDraftAdditions(sections));
}

export function writeRulesIntoKb(kbContent, additionText) {
  const add = (additionText || '').trim();
  const { rules, prose } = splitKbRulesAndProse(kbContent || '');
  if (!add) return (kbContent || '').trim() ? kbContent : '';
  return formatStoredKb(mergeRuleText(rules, add), prose);
}

export function prepareKbForPrompt(content, maxChars = KB_PROMPT_MAX_CHARS) {
  const originalLength = (content || '').length;
  const { rules, prose } = splitKbRulesAndProse(decodeHtmlEntities(content || ''));
  const rulesBlock = rules ? `${RULES_START}\n${rules}\n${RULES_END}` : '';
  const rulesLen = rulesBlock ? rulesBlock.length + (prose ? 2 : 0) : 0;

  let usedProse = prose;
  let dropped = 0;
  const proseBudget = Math.max(0, maxChars - rulesLen);
  if (prose.length > proseBudget) {
    dropped = prose.length - proseBudget;
    usedProse = prose.slice(0, proseBudget);
  }

  const text = [rulesBlock, usedProse].filter(Boolean).join('\n\n');
  return { text, rules, prose: usedProse, dropped, originalLength };
}

export function kbTextForPrompt(raw, channel) {
  const prepared = prepareKbForPrompt(raw);
  if (prepared.dropped > 0) {
    console.warn(
      `[KB PROMPT] ${channel}: knowledge base larger than the AI reads — dropped ${prepared.dropped} scraped characters. Operator rules kept in full (${prepared.rules.length} chars).`
    );
  }
  return prepared.text;
}

/**
 * Chat-specific content may replace scraped prose, never the operator rules.
 * Main-KB rules are prepended. If chatContent also has a rules block, labels
 * are merged (main wins on overlap) so the head is not duplicated.
 */
export function assembleChatKb(mainContent, chatContent) {
  const chatRaw = (chatContent || '').trim();
  if (!chatRaw) {
    return { content: mainContent || '', isChatSpecific: false };
  }
  const { rules: mainRules } = splitKbRulesAndProse(decodeHtmlEntities(mainContent || ''));
  const { rules: chatRules, prose: chatProse } = splitKbRulesAndProse(decodeHtmlEntities(chatRaw));
  const rules = mergeRuleText(chatRules, mainRules);
  return { content: formatStoredKb(rules, chatProse), isChatSpecific: true };
}

export function describeKbTruncation(content) {
  const prepared = prepareKbForPrompt(content);
  if (prepared.dropped <= 0) return null;
  return `Your knowledge base is larger than what the AI reads — ${prepared.dropped} characters were not used. Operator rules are always included; only scraped pages were trimmed.`;
}
